import { useMemo, useCallback, useEffect } from 'react';
import * as statUtils from '../core/st';
import {
    pruneLevelUnavailableSpellCriteria,
    canPruneSpellCriteriaForCurrentClass,
} from '../core/spellCriteriaPrune';
import { describeInitialBuildValidity } from '../core/initialBuildValidity';

const DEFAULT_LVL = 200;

/**
 * Build panel wiring for the Generator page — level control, empty/reset
 * build actions, character-render capture, and the invalid-build gating
 * text. See docs/generator-slicing.md cluster D.
 *
 * The level↔criteria prune helpers live in core/spellScoreTerms.js (shared
 * with cluster B's criteria sheet) rather than here, so both sides call one
 * implementation instead of diverging copies.
 *
 * Does NOT own `isServerUpdateRef` (shared across clusters A/D/F) or
 * `canGenerate` (cluster A) — callers read `lvl`/`blockingInvalidBuildReasonText`
 * off this hook's return value to keep those working.
 */
export function useBuildPanelControls({
    build,
    canEditBuild,
    setBuild,
    genConfig,
    setGenConfig,
    lang,
    spellScoringData,
    spellTypeId,
}) {
    const stats = useMemo(() => (build ? build.getStats() : {}), [build]);
    const lvlStat = useMemo(() => stats[statUtils.LVL_ID], [stats]);
    const lvl = useMemo(() => build?.lvl || DEFAULT_LVL, [build]);

    const invalidBuildStatus = useMemo(() => describeInitialBuildValidity(build, {
        reqLvl: lvl,
        lockedSlots: genConfig?.lockedSlots || {},
        lang,
    }), [build, lvl, genConfig?.lockedSlots, lang]);

    const blockingInvalidBuildReasonText = useMemo(() => (
        invalidBuildStatus?.blocking ? invalidBuildStatus.detail : null
    ), [invalidBuildStatus]);

    const handleResetBuildSlots = useCallback(() => {
        if (!build || !canEditBuild) return;
        const lockedSlots = genConfig?.lockedSlots || {};
        const nextBuild = build.clearUnlockedSlots(lockedSlots);
        setBuild(nextBuild);
    }, [build, canEditBuild, genConfig, setBuild]);

    const handleResetBuildAndLocks = useCallback(() => {
        if (!build || !canEditBuild) return;

        // First unlock all locked slots
        const hasLockedSlots = genConfig?.lockedSlots && Object.keys(genConfig.lockedSlots).length > 0;
        if (hasLockedSlots) {
            setGenConfig(genConfig.update('lockedSlots', {}));
        }

        // Then clear the whole build
        const clearedBuild = build.clearBuildSlots();
        setBuild(clearedBuild);
    }, [build, canEditBuild, genConfig, setBuild, setGenConfig]);

    const handleCharacterViewChange = useCallback((view) => {
        if (!build || !canEditBuild || typeof build.setCharacterView !== 'function') return;
        const next = build.setCharacterView(view);
        if (next === build) return;
        setBuild(next);
    }, [build, canEditBuild, setBuild]);

    const handleCharacterRenderCaptured = useCallback((snapshot) => {
        if (!build || !canEditBuild || typeof build.setCharacterRender !== 'function') return;
        const currentRender = build.getCharacterRender?.() ?? null;
        if (!snapshot) {
            if (!currentRender) return;
            setBuild(build.setCharacterRender(null));
            return;
        }
        if (
            currentRender?.key === snapshot.key
            && currentRender?.dataUrl === snapshot.dataUrl
        ) {
            return;
        }
        setBuild(build.setCharacterRender(snapshot));
    }, [build, canEditBuild, setBuild]);

    // Handle level change for the build
    const handleLevelChange = useCallback((newLevel) => {
        if (!build || !canEditBuild) return;
        const newBuild = build.setLvl(newLevel);
        if (!canPruneSpellCriteriaForCurrentClass(newBuild, spellTypeId, spellScoringData)) {
            setBuild(newBuild);
            return;
        }
        const prunedBuild = pruneLevelUnavailableSpellCriteria(newBuild, spellScoringData, newLevel);
        setBuild(prunedBuild);
    }, [build, canEditBuild, setBuild, spellScoringData, spellTypeId]);

    // Companion effect: re-runs the same prune on any spellScoringData/spellTypeId
    // change (e.g. spell data finishing a fetch after class switch), not just
    // explicit level edits via handleLevelChange above.
    useEffect(() => {
        if (!build || !canEditBuild) return;
        if (!canPruneSpellCriteriaForCurrentClass(build, spellTypeId, spellScoringData)) return;
        const prunedBuild = pruneLevelUnavailableSpellCriteria(build, spellScoringData, lvl);
        if (prunedBuild !== build) setBuild(prunedBuild);
    }, [build, canEditBuild, setBuild, spellScoringData, spellTypeId, lvl]);

    return {
        lvl,
        lvlStat,
        invalidBuildStatus,
        blockingInvalidBuildReasonText,
        handleResetBuildSlots,
        handleResetBuildAndLocks,
        handleCharacterRenderCaptured,
        handleCharacterViewChange,
        handleLevelChange,
    };
}
